import { supabase } from '../../lib/supabase';
import { Activity, ActivityFeedData } from '../../types/dashboard/ActivityFeed';
import { config } from '../../config/env';

class ActivityService {
  private readonly TABLE_NAME = 'user_activity_logs';

  /**
   * Get user activities with pagination
   */
  async getUserActivities(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<Activity[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select(`
          id,
          activity_type,
          title,
          description,
          related_user_id,
          activity_data,
          is_read,
          created_at,
          users!user_activities_related_user_id_fkey (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching user activities:', error);
        throw error;
      }

      return data.map(this.transformActivity);
    } catch (error) {
      console.error('Failed to get user activities:', error);
      throw error;
    }
  }

  /**
   * Mark activity as read
   */
  async markActivityAsRead(activityId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .update({ is_read: true })
        .eq('id', activityId);

      if (error) {
        console.error('Error marking activity as read:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to mark activity as read:', error);
      throw error;
    }
  }

  /**
   * Get unread activity count
   */
  async getUnreadActivityCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Error getting unread activity count:', error);
        throw error;
      }

      return count || 0;
    } catch (error) {
      console.error('Failed to get unread activity count:', error);
      throw error;
    }
  }

  /**
   * Create a new activity
   */
  async createActivity(activity: Omit<Activity, 'id' | 'timestamp'>): Promise<Activity> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .insert({
          user_id: activity.user?.id,
          activity_type: activity.type,
          title: activity.title,
          description: activity.description,
          related_user_id: activity.user?.id,
          activity_data: activity.metadata || {}
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating activity:', error);
        throw error;
      }

      return this.transformActivity(data);
    } catch (error) {
      console.error('Failed to create activity:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time activity updates
   */
  subscribeToActivityUpdates(
    userId: string,
    callback: (activity: Activity) => void
  ) {
    const subscription = supabase
      .channel(`user_activities_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: this.TABLE_NAME,
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const activity = this.transformActivity(payload.new);
          callback(activity);
        }
      )
      .subscribe();

    return subscription;
  }

  /**
   * Transform database record to ActivityItem
   */
  private transformActivity = (record: any): Activity => {
    return {
      id: record.id,
      type: record.activity_type,
      title: record.title,
      description: record.description,
      timestamp: new Date(record.created_at),
      user: record.users ? {
        id: record.users.id,
        name: `${record.users.first_name || ''} ${record.users.last_name || ''}`.trim() || record.users.email,
        photo: record.users.photo_url
      } : undefined,
      metadata: {
        isRead: record.is_read,
        ...record.activity_data
      }
    };
  };

  /**
   * Get activity feed data (activities + metadata)
   */
  async getActivityFeedData(userId: string): Promise<ActivityFeedData> {
    try {
      const [activities, unreadCount] = await Promise.all([
        this.getUserActivities(userId, 20, 0),
        this.getUnreadActivityCount(userId)
      ]);

      return {
        activities,
        totalCount: activities.length,
        unreadCount,
        hasMore: activities.length === 20
      };
    } catch (error) {
      console.error('Failed to get activity feed data:', error);
      throw error;
    }
  }

  /**
   * Mark all activities as read
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all activities as read:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to mark all activities as read:', error);
      throw error;
    }
  }

  /**
   * Delete old activities (cleanup)
   */
  async deleteOldActivities(userId: string, olderThanDays: number = 30): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

      const { error } = await supabase
        .from(this.TABLE_NAME)
        .delete()
        .eq('user_id', userId)
        .lt('created_at', cutoffDate.toISOString());

      if (error) {
        console.error('Error deleting old activities:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to delete old activities:', error);
      throw error;
    }
  }
}

export const activityService = new ActivityService();
